import React from 'react';
import styles from './Onboarding.module.css';
import * as utils from '../../utils/utils';

export default class Onboarding extends React.Component {

	constructor(props) {
		super(props);

		this.steps = {
			Intro: 1,
			Resume: 2,
			Availability: 3,
			Tabs: 4,
            WelcomeAndFirstLiveResume: 5,
		};

		this.state = {
			currentStep: this.steps.Intro,

			isDragActive: false,
			resumeFile: null,
			uploadProgress: 0,
			isUploading: false,
			isProcessingResume: false,

			availabilityItems: [''],
			tabItems: [''],
			activeAvailabilityIndex: 0,
			activeTabIndex: 0,
			isSavingAvailability: false,
			isSavingTabs: false,

			errorText: '',
			infoText: 'Upload your latest resume (pdf) and let HQ handle the rest',
		};
	}

	beginOnboarding = () => {
		this.setState({
			currentStep: this.steps.Resume,
			errorText: '',
			infoText: 'Upload your latest resume (pdf) and let HQ handle the rest',
		});
	};

	getErrorMessage = (error, fallbackText) => {
		if (error && typeof error === 'object' && typeof error.message === 'string' && error.message.trim() !== '') {
			return error.message;
		}

		if (typeof error === 'string' && error.trim() !== '') {
			return error;
		}

		return fallbackText;
	};

	getValidatedPdfFile = (file) => {
		if (!file) {
			return null;
		}

		const isPdfMime = file.type === 'application/pdf';
		const isPdfName = file.name.toLowerCase().endsWith('.pdf');

		if (!isPdfMime && !isPdfName) {
			return null;
		}

		return file;
	};

	onFileChange = (event) => {
		const file = event.target.files && event.target.files[0];

		if (!file) {
			this.setState({
				resumeFile: null,
				errorText: '',
			});
			return;
		}

		const validatedFile = this.getValidatedPdfFile(file);

		if (!validatedFile) {
			this.setState({
				resumeFile: null,
				errorText: 'Please select a PDF file.',
			});
			return;
		}

		this.setState({
			resumeFile: validatedFile,
			errorText: '',
		});
	};

	onDragOver = (event) => {
		event.preventDefault();
		event.stopPropagation();

		if (!this.state.isDragActive) {
			this.setState({ isDragActive: true });
		}
	};

	onDragLeave = (event) => {
		event.preventDefault();
		event.stopPropagation();
		this.setState({ isDragActive: false });
	};

	onDrop = (event) => {
		event.preventDefault();
		event.stopPropagation();

		const file = event.dataTransfer?.files && event.dataTransfer.files[0];
		const validatedFile = this.getValidatedPdfFile(file);

		if (!validatedFile) {
			this.setState({
				isDragActive: false,
				resumeFile: null,
				errorText: 'Please drop a PDF file.',
			});
			return;
		}

		this.setState({
			isDragActive: false,
			resumeFile: validatedFile,
			errorText: '',
		});
	};

	uploadAndProcessResume = async () => {
		const {
			resumeFile,
		} = this.state;

		if (!resumeFile) {
			this.setState({ errorText: 'Please choose a PDF resume first.' });
			return;
		}

		this.setState({
			isUploading: true,
			isProcessingResume: false,
			uploadProgress: 0,
			errorText: '',
			infoText: 'Preparing secure upload...',
		});

		try {
			const uploadData = await utils.sendRequest('/resume/getResumeUploadData', {});
			const {
				uuidKey,
				originalMediaSignedUrl,
			} = uploadData || {};

			if (!uuidKey || !originalMediaSignedUrl) {
				throw new Error('Could not prepare resume upload data.');
			}

			this.setState({ infoText: 'Uploading your resume...' });

            await utils.uploadFileToSignedUrl(originalMediaSignedUrl, resumeFile, {
                contentType: 'application/pdf',
                onProgress: (progress) => {
                    this.setState({ uploadProgress: progress });
                }
            });

			this.setState({
				isUploading: false,
				isProcessingResume: true,
				infoText: 'Processing your resume...',
			});

			await utils.sendRequest('/resume/processResume', { uuidKey });

			// this.setState({
			// 	currentStep: this.steps.Availability,
			// 	isUploading: false,
			// 	isProcessingResume: false,
			// 	uploadProgress: 0,
			// 	errorText: '',
			// 	infoText: 'In plain English, briefly describe what you\'re looking for',
			// });
		}
		catch (error) {
			console.log('resume onboarding error', error);
			this.setState({
				isUploading: false,
				isProcessingResume: false,
				errorText: this.getErrorMessage(error, 'Could not upload and process resume. Please try again.'),
				infoText: 'Upload your latest resume (pdf) and let HQ handle the rest',
			});
		}
	};

	onListItemChange = (stateKey, index, value) => {
		this.setState((prevState) => {
			const nextItems = [...prevState[stateKey]];
			nextItems[index] = value;

			if (index === nextItems.length - 1 && value.trim() !== '' && nextItems.length < 5) {
				nextItems.push('');
			}

			return {
				[stateKey]: nextItems,
				errorText: '',
			};
		});
	};

	onListItemFocus = (stateKey, index) => {
		if (stateKey === 'availabilityItems') {
			this.setState({ activeAvailabilityIndex: index });
		}
		else if (stateKey === 'tabItems') {
			this.setState({ activeTabIndex: index });
		}
	};

	getExampleTargetIndex = (items, activeIndex) => {
		if (Number.isInteger(activeIndex) && activeIndex >= 0 && activeIndex < items.length) {
			return activeIndex;
		}

		const emptyIndex = items.findIndex((item) => item.trim() === '');

		if (emptyIndex >= 0) {
			return emptyIndex;
		}

		return 0;
	};

	applyExampleText = (text) => {
		const value = typeof text === 'string' ? text.trim() : '';

		if (value === '') {
			return;
		}

		const { currentStep, availabilityItems, tabItems, activeAvailabilityIndex, activeTabIndex } = this.state;

		if (currentStep === this.steps.Availability) {
			const nextIndex = this.getExampleTargetIndex(availabilityItems, activeAvailabilityIndex);
			this.setState((prevState) => {
				const nextItems = [...prevState.availabilityItems];
				nextItems[nextIndex] = value;

				return {
					availabilityItems: nextItems,
					activeAvailabilityIndex: nextIndex,
					errorText: '',
				};
			});
		}
		else if (currentStep === this.steps.Tabs) {
			const nextIndex = this.getExampleTargetIndex(tabItems, activeTabIndex);
			this.setState((prevState) => {
				const nextItems = [...prevState.tabItems];
				nextItems[nextIndex] = value;

				return {
					tabItems: nextItems,
					activeTabIndex: nextIndex,
					errorText: '',
				};
			});
		}
	};

	getSanitizedItems = (items) => {
		if (!Array.isArray(items)) {
			return [];
		}

		return items
			.map((item) => (typeof item === 'string' ? item.trim() : ''))
			.filter((item) => item !== '');
	};

	saveAvailabilityAndContinue = async () => {
		if (this.state.isSavingAvailability) {
			return;
		}

		const availabilityTexts = this.getSanitizedItems(this.state.availabilityItems);

		if (availabilityTexts.length === 0) {
			this.setState({ errorText: 'Please add at least one availability item, or skip this step.' });
			return;
		}

		this.setState({
			isSavingAvailability: true,
			errorText: '',
		});

		try {
			await utils.sendRequest('/availability/saveAvailability', {
				availabilityTexts,
			});

			this.setState({
				currentStep: this.steps.Tabs,
				isSavingAvailability: false,
				errorText: '',
				infoText: 'Briefly describe what you want to keep a tab on',
			});
		}
		catch (error) {
			console.log('save availability error', error);
			this.setState({
				isSavingAvailability: false,
				errorText: this.getErrorMessage(error, 'Could not save availability. Please try again.'),
			});
		}
	};

	skipAvailability = () => {
		this.setState({
			currentStep: this.steps.Tabs,
			errorText: '',
			infoText: 'Briefly describe what you want to keep a tab on',
		});
	};

	saveTabsAndFinish = async () => {
		if (this.state.isSavingTabs) {
			return;
		}

		const tabTexts = this.getSanitizedItems(this.state.tabItems);

		if (tabTexts.length === 0) {
			this.setState({ errorText: 'Please add at least one tab item, or skip this step.' });
			return;
		}

		this.setState({
			isSavingTabs: true,
			errorText: '',
		});

		try {
			await utils.sendRequest('/tab/saveTab', {
				tabTexts,
			});

			window.location.href = '/dashboard';
		}
		catch (error) {
			console.log('save tab error', error);
			this.setState({
				isSavingTabs: false,
				errorText: this.getErrorMessage(error, 'Could not save tabs. Please try again.'),
			});
		}
	};

	skipTabs = () => {
		window.location.href = '/dashboard';
	};

	renderStepDots = () => {
		const { currentStep } = this.state;

		return (
			<div className={styles.stepRow}>
				<div className={`${styles.stepDot} ${currentStep >= this.steps.Resume ? styles.stepDotActive : ''}`} />
				<div className={styles.stepLine} />
				<div className={`${styles.stepDot} ${currentStep >= this.steps.Availability ? styles.stepDotActive : ''}`} />
				<div className={styles.stepLine} />
				<div className={`${styles.stepDot} ${currentStep >= this.steps.Tabs ? styles.stepDotActive : ''}`} />
			</div>
		);
	};

	renderResumeStep = () => {
		const {
			isDragActive,
			resumeFile,
			uploadProgress,
			isUploading,
			isProcessingResume,
		} = this.state;

		return (
			<div className={styles.form}>
				<div className={styles.fieldGroup}>
					<label className={styles.label} htmlFor='onboarding-resume-file'>Resume PDF</label>
					<div
						className={`${styles.dropZone} ${isDragActive ? styles.dropZoneActive : ''} ${(isUploading || isProcessingResume) ? styles.dropZoneDisabled : ''}`}
						onDragOver={this.onDragOver}
						onDragLeave={this.onDragLeave}
						onDrop={this.onDrop}
					>
						<input
							id='onboarding-resume-file'
							type='file'
							accept='application/pdf,.pdf'
							className={styles.hiddenFileInput}
							onChange={this.onFileChange}
							disabled={isUploading || isProcessingResume}
						/>
						<label className={styles.dropZoneInner} htmlFor='onboarding-resume-file'>
							<span className={styles.dropZoneTitle}>Drag and drop your resume here</span>
							<span className={styles.dropZoneText}>or click to choose a PDF file</span>
						</label>
					</div>

					{
						resumeFile &&
						<p className={styles.fileName}>{resumeFile.name}</p>
					}
				</div>

				<div className={styles.progressWrap}>
					<div className={styles.progressTrack}>
						<div className={styles.progressFill} style={{ width: `${uploadProgress}%` }} />
					</div>
					<div className={styles.progressText}>
						{isProcessingResume ? 'Processing resume...' : `Upload progress: ${uploadProgress}%`}
					</div>
				</div>

				<button
					className={styles.primaryButton}
					type='button'
					onClick={this.uploadAndProcessResume}
					disabled={isUploading || isProcessingResume}
				>
					{isProcessingResume ? 'Processing...' : (isUploading ? 'Uploading...' : 'Upload & Continue')}
				</button>
			</div>
		);
	};

	renderExamples = (items) => {
		return (
			<div className={styles.examplesBox}>
				<p className={styles.examplesTitle}>Examples</p>
				<div className={styles.exampleList}>
					{items.map((item, index) => (
						<button
							key={index}
							type='button'
							className={styles.exampleButton}
							onClick={() => this.applyExampleText(item)}
						>
							{item}
						</button>
					))}
				</div>
			</div>
		);
	};

	renderTextListStep = (config) => {
		const {
			stateKey,
			placeholder,
			items,
			onSave,
			onSkip,
			isSaving,
		} = config;

		return (
			<div className={styles.form}>
				{items.map((item, index) => (
					<div key={`${stateKey}-${index}`} className={styles.fieldGroup}>
						<label className={styles.label} htmlFor={`${stateKey}-item-${index + 1}`}>Item {index + 1}</label>
						<input
                            type='text'
							id={`${stateKey}-item-${index + 1}`}
							rows={3}
                            className={styles.textInput}
							placeholder={placeholder}
							value={item}
							onChange={(event) => this.onListItemChange(stateKey, index, event.target.value)}
                            onFocus={() => this.onListItemFocus(stateKey, index)}
							disabled={isSaving}
						/>
					</div>
				))}

				<div className={styles.actionRow}>
					<span className={styles.helperText}>{items.length}/5 items</span>
				</div>

				<button
					className={styles.primaryButton}
					type='button'
					onClick={onSave}
					disabled={isSaving}
				>
					{isSaving ? 'Saving...' : 'Save & Continue'}
				</button>

				<button
					className={styles.secondaryButton}
					type='button'
					onClick={onSkip}
					disabled={isSaving}
				>
					Skip
				</button>
			</div>
		);
	};

	renderCurrentStep = () => {
		const {
			currentStep,
			availabilityItems,
			tabItems,
			isSavingAvailability,
			isSavingTabs,
		} = this.state;

		if (currentStep === this.steps.Resume) {
			return this.renderResumeStep();
		}

		if (currentStep === this.steps.Availability) {
			return this.renderTextListStep({
				stateKey: 'availabilityItems',
				placeholder: 'Describe a role, location, work style, and compensation or timeline.',
				items: availabilityItems,
				onSave: this.saveAvailabilityAndContinue,
				onSkip: this.skipAvailability,
				isSaving: isSavingAvailability,
			});
		}

		return this.renderTextListStep({
			stateKey: 'tabItems',
			placeholder: 'Describe what signals or opportunities you want HQ to track for you.',
			items: tabItems,
			onSave: this.saveTabsAndFinish,
			onSkip: this.skipTabs,
			isSaving: isSavingTabs,
		});
	};

	getStepHeader = () => {
		const { currentStep } = this.state;

		if (currentStep === this.steps.Resume) {
			return {
				title: 'Let HQ know who you are',
				subtitle: 'Upload your latest resume (pdf) and let HQ handle the rest',
			};
		}

		if (currentStep === this.steps.Availability) {
			return {
				title: 'Your next opportunity',
				subtitle: 'In plain English, briefly describe what you\'re looking for',
			};
		}

		return {
			title: 'Keep a tab on...',
			subtitle: 'Briefly describe what you want to keep a tab on',
		};
	};

	render() {
		const {
			currentStep,
			errorText,
		} = this.state;

		const stepHeader = this.getStepHeader();

		return (
			<div className={styles.pageContainer}>
				<div className={styles.glowTop} />
				<div className={styles.glowBottom} />

				<div className={styles.layoutShell}>
					{
						currentStep === this.steps.Intro ? (
							<section className={`${styles.leftPanel} ${styles.centerPanel}`}>
								<div className={styles.brandBadge}>HQVibe</div>
								<div className={styles.introPill}>Step 1 of 4</div>
								<h1 className={styles.title}>Set your hiring signal in a few quick steps.</h1>
								<p className={styles.subtitle}>
									We use your resume and intent to match you with high-context opportunities and people worth knowing.
								</p>
								<button className={styles.primaryButton} type='button' onClick={this.beginOnboarding}>
									Begin
								</button>
							</section>
						) : (
							<section className={styles.cardPanel}>
								<div className={styles.stepRow}>
									<div className={`${styles.stepDot} ${currentStep >= this.steps.Resume ? styles.stepDotActive : ''}`} />
									<div className={styles.stepLine} />
									<div className={`${styles.stepDot} ${currentStep >= this.steps.Availability ? styles.stepDotActive : ''}`} />
									<div className={styles.stepLine} />
									<div className={`${styles.stepDot} ${currentStep >= this.steps.Tabs ? styles.stepDotActive : ''}`} />
								</div>

								<h2 className={styles.formTitle}>{stepHeader.title}</h2>
								<p className={styles.formText}>{stepHeader.subtitle}</p>

								{
									currentStep === this.steps.Availability &&
									this.renderExamples([
										'Software job, hybrid or remote in US, min $100k',
										'Paid internship in Biology major, near Boston',
										'Director of Marketing anywhere',
									])
								}

								{
									currentStep === this.steps.Tabs &&
									this.renderExamples([
										'Let me know if someone is looking for an internship',
										'Who else is doing AI projects',
										'Founders looking to raise in consumer, social',
									])
								}

								{this.renderCurrentStep()}

								{
									errorText !== '' &&
									<div className={styles.errorBox}>{errorText}</div>
								}
							</section>
						)
					}
				</div>
			</div>
		);
	}
}
